/* =========================================================================
 * IBC 2.0 — PRODUCT TILE
 * -------------------------------------------------------------------------
 * Swipeable variant imagery on a product tile. Everything this file does is
 * driven by markup that snippets/ibc-product-card.liquid has already
 * rendered:
 *
 *   [data-ibc-tile]                the card, only when it has 2+ distinct
 *                                  variant media
 *   [data-ibc-tile-track]          the scroll-snap track
 *   [data-ibc-tile-frame]          one frame per distinct media, carrying
 *                                  data-ibc-variant-id / -variant-title /
 *                                  -available and a <template> holding that
 *                                  variant's price, rendered by ibc-price
 *   [data-ibc-tile-price]          the live region in the tile foot
 *   [data-ibc-tile-dashes]         the position marker
 *   [data-ibc-tile-sold-out]       the sold-out flag
 *   [data-ibc-quick-view]          the expand control (its variant id is
 *                                  kept in step here)
 *
 * NO MONEY IS FORMATTED HERE. Each frame's price arrives as markup from
 * `ibc-price`, so the tile, the product page and the cart cannot disagree.
 *
 * WHY THE TRACK IS A NATIVE SCROLLER
 * ----------------------------------
 * A hand-rolled drag would have to decide, on every touchmove, whether the
 * gesture is horizontal or vertical — which is exactly how carousels end up
 * hijacking the page scroll. `overflow-x: auto` with `scroll-snap-type: x
 * mandatory` gives the browser that decision back: a vertical drag scrolls
 * the page, a horizontal one scrolls the track, and neither fights the
 * other. This file only listens.
 *
 * TAP VERSUS SWIPE
 * ----------------
 * The track sits inside the card's `<a>`, so a mouse drag across a frame
 * would otherwise land as a click on the link and open the product. Every
 * pointerdown records its position and the track's scrollLeft; a click is
 * suppressed if the pointer moved more than MOVE_PX or the track scrolled
 * at all since then. Touch drags do not synthesise a click while a scroller
 * is moving, but the same guard covers them for free.
 *
 * THE ARROWS (QA D-02)
 * ---------------------
 * `prev`/`next` step ONE frame from wherever the track actually is. The base
 * is read fresh on every press — `track.scrollLeft` rounded to the nearest
 * frame — so a swipe and an arrow press cannot disagree, and nothing is
 * carried over from the last press. Two exceptions, both narrower than the
 * live position rather than a substitute for it: while a programmatic scroll
 * is still travelling, the frame it is travelling TO is the base (otherwise a
 * quick second press reads a half-finished animation and steps to the same
 * frame twice), and when the track cannot scroll at all the painted frame is
 * the base. Steps clamp at the ends; `prev` on the first frame and `next` on
 * the last are `disabled`, and the card is served with `prev` disabled.
 *
 * EVERYTHING IS DELEGATED at the document, in the capture phase, so tiles
 * that arrive later — the filtered grid is re-fetched and swapped whole by
 * assets/ibc-filters.js — work with no re-initialisation and no observer.
 * The first frame's price, dash and variant id are already correct in the
 * server-rendered markup, so there is no start-up pass at all.
 * ====================================================================== */
(function () {
  'use strict';

  if (window.IbcTile) return;

  var MOVE_PX = 8;
  var SETTLE_MS = 90;
  /* How long a programmatic scroll is allowed to be "in flight" before the
   * live scroll position takes over again. Long enough for a smooth scroll
   * across one frame, short enough that a stuck one cannot wedge the arrows. */
  var TRAVEL_MS = 800;
  /* If the smooth scroll has not moved the track by then, place it by hand. */
  var LAND_MS = 420;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function trackOf(node) {
    return node && node.closest ? node.closest('[data-ibc-tile-track]') : null;
  }

  function tileOf(node) {
    return node && node.closest ? node.closest('[data-ibc-tile]') : null;
  }

  function framesOf(track) {
    return toArray(track.querySelectorAll('[data-ibc-tile-frame]'));
  }

  /* Which frame is in view, READ FROM THE TRACK ITSELF? Nearest left edge to
   * the current scroll offset, measured against the first frame so a padded
   * track still reads right. Returns -1 when the track cannot scroll at all,
   * because then scrollLeft is 0 whatever frame is showing and answering "0"
   * would be a lie — see currentIndex(). */
  function liveIndex(track) {
    var frames = framesOf(track);
    if (!frames.length) return -1;
    if (track.scrollWidth - track.clientWidth <= 1) return -1;
    var origin = frames[0].offsetLeft;
    var left = track.scrollLeft;
    var best = 0;
    var bestDistance = Infinity;
    frames.forEach(function (frame, i) {
      var distance = Math.abs(frame.offsetLeft - origin - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  /* The frame the tile is currently PAINTED on. Written by paint() from the
   * live scroll position on every settle, so it is a record of the DOM, not
   * a variable captured when a handler was created. */
  function paintedIndex(track) {
    var tile = tileOf(track);
    var frames = framesOf(track);
    if (!tile || !frames.length) return 0;
    var at = parseInt(tile.getAttribute('data-ibc-tile-at'), 10);
    if (isNaN(at)) return 0;
    return Math.max(0, Math.min(frames.length - 1, at));
  }

  /* A programmatic scroll is not instant, so a second arrow press that lands
   * mid-animation would otherwise read the position it started from and step
   * to the same frame twice. One record per track, cleared the moment the
   * track arrives, the moment the visitor touches it, or after TRAVEL_MS. */
  var travel = typeof WeakMap === 'function' ? new WeakMap() : null;

  function travelSet(track, index) {
    var record = { index: index, until: Date.now() + TRAVEL_MS };
    if (travel) travel.set(track, record);
    else track._ibcTravel = record;
  }

  function travelGet(track) {
    var record = travel ? travel.get(track) : track._ibcTravel;
    if (!record) return null;
    if (Date.now() > record.until) {
      travelClear(track);
      return null;
    }
    return record;
  }

  function travelClear(track) {
    if (travel) travel['delete'](track);
    else track._ibcTravel = null;
  }

  /* WHERE THE TRACK ACTUALLY IS. Live scroll position first; the frame a
   * programmatic scroll is still travelling to while it is in flight; the
   * painted frame only when the track cannot scroll at all. Nothing here is
   * remembered from a previous press. */
  function currentIndex(track) {
    var frames = framesOf(track);
    if (frames.length < 2) return 0;

    var moving = travelGet(track);
    if (moving) return Math.max(0, Math.min(frames.length - 1, moving.index));

    var live = liveIndex(track);
    if (live >= 0) return live;

    return paintedIndex(track);
  }

  /* Steps CLAMP at the ends — no wrapping. `prev` on frame 1 and `next` on
   * the last frame are disabled controls anyway (see paint()), and wrapping
   * from a stale 0 is exactly what made every `prev` jump to the last frame
   * (QA D-02). */
  function goTo(track, index) {
    var frames = framesOf(track);
    if (!frames.length) return;
    var i = Math.max(0, Math.min(frames.length - 1, index));
    var left = frames[i].offsetLeft - frames[0].offsetLeft;

    travelSet(track, i);
    try {
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    } catch (error) {
      track.scrollLeft = left;
    }

    /* Paint immediately rather than waiting for the scroll to settle: an
     * arrow press should not have a 90ms lag on the price. */
    paint(track, i);

    /* Belt. If the scroll never actually started, put the track where it was
     * asked to go, so the NEXT press still steps from the right frame rather
     * than from a track stuck at zero. Skipped if the visitor has taken over
     * in the meantime, which clears the travel record. */
    window.setTimeout(function () {
      var moving = travelGet(track);
      if (!moving || moving.index !== i) return;
      if (Math.abs(track.scrollLeft - left) > 1) track.scrollLeft = left;
      travelClear(track);
      paint(track, currentIndex(track));
    }, LAND_MS);
  }

  /* ---------------------------------------------------------------- paint -- */

  function paint(track, index) {
    var tile = tileOf(track);
    if (!tile) return;
    var frames = framesOf(track);
    var frame = frames[index];
    if (!frame) return;

    /* The ends. Done before the repaint guard so the controls are still put
     * right on the very first paint of an already-correct frame. */
    var prevBtn = tile.querySelector('[data-ibc-tile-prev]');
    var nextBtn = tile.querySelector('[data-ibc-tile-next]');
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index >= frames.length - 1;

    if (String(tile.getAttribute('data-ibc-tile-at')) === String(index)) return;
    tile.setAttribute('data-ibc-tile-at', String(index));

    frames.forEach(function (node, i) {
      node.classList.toggle('is-on', i === index);
    });

    var dashes = tile.querySelector('[data-ibc-tile-dashes]');
    if (dashes) {
      toArray(dashes.children).forEach(function (dash, i) {
        dash.classList.toggle('is-on', i === index);
      });
    }

    /* Price and variant name. Both live inside the one `aria-live` region,
     * so a screen-reader hears the name and the figure as a single update. */
    var priceSlot = tile.querySelector('[data-ibc-tile-price-slot]');
    var template = frame.querySelector('[data-ibc-frame-price]');
    if (priceSlot && template) priceSlot.innerHTML = template.innerHTML;

    var name = tile.querySelector('[data-ibc-tile-variant-name]');
    if (name) name.textContent = frame.getAttribute('data-ibc-variant-title') || '';

    var available = frame.getAttribute('data-ibc-available') !== 'false';
    var soldOut = tile.querySelector('[data-ibc-tile-sold-out]');
    if (soldOut) soldOut.hidden = available;

    /* The quick view must open on the variant the customer is looking at. */
    var control = tile.querySelector('[data-ibc-quick-view]');
    var variantId = frame.getAttribute('data-ibc-variant-id');
    if (control && variantId) control.setAttribute('data-ibc-variant-id', variantId);
  }

  /* --------------------------------------------------------------- events -- */

  var timers = typeof WeakMap === 'function' ? new WeakMap() : null;

  /* The track has stopped moving. If it stopped where a programmatic scroll
   * was sending it, that journey is over and the live position takes back
   * over; either way the paint follows the track, which is what keeps a
   * SWIPE in step with the arrows. */
  function onSettled(track) {
    var live = liveIndex(track);
    var moving = travelGet(track);
    if (moving && live >= 0 && live === moving.index) {
      travelClear(track);
      moving = null;
    }
    paint(track, currentIndex(track));
  }

  function settle(track) {
    if (timers) {
      window.clearTimeout(timers.get(track));
      timers.set(
        track,
        window.setTimeout(function () {
          onSettled(track);
        }, SETTLE_MS)
      );
      return;
    }
    window.clearTimeout(track._ibcTimer);
    track._ibcTimer = window.setTimeout(function () {
      onSettled(track);
    }, SETTLE_MS);
  }

  /* `scroll` does not bubble, but it does capture. */
  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches('[data-ibc-tile-track]')) return;
      settle(target);
    },
    true
  );

  /* Movement guard. One record per tile, replaced on every pointerdown. */
  var pointerState = null;

  document.addEventListener(
    'pointerdown',
    function (event) {
      var track = trackOf(event.target);
      if (!track) {
        pointerState = null;
        return;
      }
      /* The visitor has taken the track over; any programmatic journey is
       * abandoned so the live scroll position is what the arrows read next. */
      travelClear(track);
      pointerState = {
        track: track,
        x: event.clientX,
        y: event.clientY,
        left: track.scrollLeft,
        moved: false
      };
    },
    true
  );

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!pointerState) return;
      if (Math.abs(event.clientX - pointerState.x) > MOVE_PX) pointerState.moved = true;
      if (Math.abs(event.clientY - pointerState.y) > MOVE_PX) pointerState.moved = true;
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var prev = event.target.closest('[data-ibc-tile-prev]');
      var next = event.target.closest('[data-ibc-tile-next]');
      if (prev || next) {
        /* The arrows are siblings of the card's link, never inside it, so
         * this is belt only — but a stray click must not navigate. */
        event.preventDefault();
        event.stopPropagation();
        var tile = tileOf(prev || next);
        var arrowTrack = tile ? tile.querySelector('[data-ibc-tile-track]') : null;
        if (!arrowTrack) return;
        goTo(arrowTrack, currentIndex(arrowTrack) + (next ? 1 : -1));
        return;
      }

      var track = trackOf(event.target);
      if (!track || !pointerState || pointerState.track !== track) return;
      var dragged = pointerState.moved || Math.abs(track.scrollLeft - pointerState.left) > 1;
      pointerState = null;
      if (!dragged) return;
      /* A swipe is not a tap: do not follow the card's link. */
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  /* A drag that starts on an image inside a link tries to drag the link.
   * The images carry draggable="false"; this covers the anchor itself. */
  document.addEventListener(
    'dragstart',
    function (event) {
      if (trackOf(event.target)) event.preventDefault();
    },
    true
  );

  window.IbcTile = {
    goTo: goTo,
    currentIndex: currentIndex,
    /* Repaint from the DOM. Nothing in the theme needs to call this — the
     * server-rendered first frame is already correct — but a surface that
     * moves a track by hand can settle it here. */
    sync: function (root) {
      toArray((root || document).querySelectorAll('[data-ibc-tile-track]')).forEach(function (track) {
        paint(track, currentIndex(track));
      });
    }
  };
})();
